import joborderService from '../../../services/joborderService';
import filterService from '../../../services/filterService';

import {
    setJobOrderLoading,
    setJobOrderError,
    setJobOrderList,
    setJobOrderCurrent,
    setJobOrderPagination,
    setJobOrderSort,
    setJobOrderFilters,
    setJobOrderDefaultFilter,
    setJobOrderSettings,
    clearJobOrderCurrent,
    clearJobOrderError,
    setJobOrderFilterLoading,
    changeJobOrderFilterCondition,
    addJobOrderFilterCondition,
    updateJobOrderFilterConditionRule,
    updateJobOrderFilterConditionValue,
    removeJobOrderFilterCondition,
    setJobOrderSelected,
    resetJobOrderFiltersAndPagination
} from './joborderSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().joborder.isSettingsInitialized) {
            dispatch(setJobOrderLoading(true));
            const response = await joborderService.getSettings();

            // Set the settings
            dispatch(setJobOrderSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setJobOrderDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setJobOrderError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setJobOrderLoading(false));
    }
};

export const fetchJoborders = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized
        const state = getState().joborder;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setJobOrderLoading(true));
        const response = await joborderService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setJobOrderList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchJoborders:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setJobOrderError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobOrderLoading(false));
    }
};


export const fetchJobOrder = (id) => async (dispatch) => {
    try {
        dispatch(setJobOrderLoading(true));
        const response = await joborderService.getOne(id);
        dispatch(setJobOrderCurrent(response.data));
    } catch (error) {
        dispatch(setJobOrderError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setJobOrderLoading(false));
    }
};

export const createJobOrder = (data) => async (dispatch) => {
    try {
        const response = await joborderService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateJobOrder = (id, data) => async (dispatch) => {
    try {
        const response = await joborderService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteJobOrder = (id) => async (dispatch) => {
    try {
        dispatch(setJobOrderLoading(true));
        const response = await joborderService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setJobOrderError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setJobOrderLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setJobOrderPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setJobOrderSort(sort));
};

export const searchJoborders = (query) => (dispatch, getState) => {
    const currentFilters = getState().joborder.filters;
    dispatch(setJobOrderFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setJobOrderDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeJobOrderFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addJobOrderFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateJobOrderFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateJobOrderFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeJobOrderFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setJobOrderFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().joborder.settings;
            dispatch(setJobOrderSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setJobOrderFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setJobOrderFilterLoading(false));
        dispatch(setJobOrderError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const createAdvancePayment = (id, data) => async () => {
    try {
        const response = await joborderService.addAdvancePayment(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setJobOrderSelected(selectedRows));
    };
};

export const clearCurrentJobOrder = () => {
    return (dispatch) => {
        dispatch(clearJobOrderCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetJobOrderFiltersAndPagination());
};